import { useEffect, useState } from "react";
import { http, fmtINR, fmtNum, fmtPct } from "@/lib/api";
import {
    ReportTable,
    ReportTHead,
    SortableTH,
    FilterBar,
    FilterSelect,
    SearchInput,
} from "@/components/ReportTable";
import {
    BarChart,
    Bar,
    ResponsiveContainer,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from "recharts";

const PROMO_STYLES = {
    EOSS: "bg-amber-500/10 text-amber-600 border-amber-500/30",
    "CASH BACK": "bg-purple-500/10 text-purple-600 border-purple-500/30",
    "NO PROMO": "bg-slate-500/10 text-slate-600 border-slate-500/30",
};

export default function EossReport() {
    const [facets, setFacets] = useState({});
    const [summary, setSummary] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState({ by: "invoice_date", dir: -1 });
    const [filters, setFilters] = useState({ promo_type: "All", state: "All", distributor: "All", category: "All" });
    const [q, setQ] = useState("");

    useEffect(() => {
        http.get("/reports/eoss/facets").then((r) => setFacets(r.data));
        http.get("/reports/eoss/summary").then((r) => setSummary(r.data));
    }, []);

    useEffect(() => {
        const params = { limit: 300, sort_by: sort.by, sort_dir: sort.dir, q };
        Object.entries(filters).forEach(([k, v]) => {
            if (v !== "All") params[k] = v;
        });
        http.get("/reports/eoss", { params }).then((r) => {
            setRows(r.data.rows);
            setTotal(r.data.total);
        });
    }, [filters, sort, q]);

    const t = summary?.totals;
    const empty = t && t.rows === 0;

    return (
        <div className="mx-auto max-w-[1560px] space-y-4 p-6">
            <div className="flex items-end justify-between">
                <div>
                    <div className="mlabel">Reports · EOSS</div>
                    <h1 className="text-[22px] font-semibold tracking-tight">EOSS / Markdown & Credit-Note Working</h1>
                    <p className="mt-1 text-[12px] text-muted-foreground">
                        Distributor-level markdown reconciliation — discount &amp; credit-note liability by promo type,
                        state, and category.
                    </p>
                </div>
                <div className="text-[11px] text-muted-foreground">
                    {t ? `${fmtNum(t.rows)} rows · ${fmtNum(rows.length)}/${fmtNum(total)} shown` : "…"}
                </div>
            </div>

            {empty && (
                <div className="rounded-sm border border-dashed border-border bg-card p-8 text-center">
                    <div className="text-sm font-semibold">No EOSS working data</div>
                    <div className="mt-1 text-[12px] text-muted-foreground">
                        Upload the EOSS credit-note workbook (DATA sheet) via Reports → Upload.
                    </div>
                </div>
            )}

            {t && !empty && (
                <>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                        <Stat label="Rows" value={fmtNum(t.rows)} />
                        <Stat label="Quantity" value={fmtNum(t.qty)} />
                        <Stat label="MRP Value" value={fmtINR(t.mrp)} />
                        <Stat label="Discount" value={fmtINR(t.discount)} tone="amber" />
                        <Stat label="Discount %" value={fmtPct(t.discount_pct)} tone="red" />
                    </div>

                    <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-12 rounded-sm border border-border bg-card p-4 lg:col-span-5">
                            <div className="mlabel mb-2">Promo Mix (Net Value)</div>
                            <div className="h-[220px]">
                                <ResponsiveContainer>
                                    <BarChart data={summary.by_promo}>
                                        <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} />
                                        <XAxis dataKey="key" stroke="hsl(var(--muted-foreground))" fontSize={10} tickLine={false} />
                                        <YAxis
                                            stroke="hsl(var(--muted-foreground))"
                                            fontSize={10}
                                            width={40}
                                            tickLine={false}
                                            tickFormatter={(v) => (v >= 1e5 ? `${(v / 1e5).toFixed(1)}L` : v)}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                background: "hsl(var(--card))",
                                                border: "1px solid hsl(var(--border))",
                                                fontSize: 12,
                                            }}
                                            formatter={(v) => fmtINR(v)}
                                        />
                                        <Bar dataKey="net" fill="hsl(var(--chart-1))" name="Net" radius={[3, 3, 0, 0]} />
                                        <Bar dataKey="discount" fill="hsl(var(--chart-5))" name="Discount" radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <div className="col-span-12 rounded-sm border border-border bg-card p-4 lg:col-span-7">
                            <div className="mlabel mb-2">Category Breakdown</div>
                            <div className="max-h-[220px] overflow-y-auto">
                                <table className="w-full text-[11px]">
                                    <thead className="sticky top-0 bg-card text-[10px] uppercase tracking-wider text-muted-foreground">
                                        <tr>
                                            <th className="px-2 py-1 text-left">Category</th>
                                            <th className="px-2 py-1 text-right">Qty</th>
                                            <th className="px-2 py-1 text-right">Discount</th>
                                            <th className="px-2 py-1 text-right">Net</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {summary.by_category.map((r) => (
                                            <tr key={r.key} className="border-t border-border">
                                                <td className="px-2 py-1 font-medium">{r.key || "—"}</td>
                                                <td className="mono-num px-2 py-1 text-right">{fmtNum(r.qty)}</td>
                                                <td className="mono-num px-2 py-1 text-right text-amber-600">
                                                    {fmtINR(r.discount)}
                                                </td>
                                                <td className="mono-num px-2 py-1 text-right font-semibold">
                                                    {fmtINR(r.net)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <FilterBar>
                <SearchInput
                    value={q}
                    onChange={setQ}
                    placeholder="Search retailer / invoice / EAN / MRM…"
                    testid="eoss-search"
                />
                <FilterSelect
                    label="Promo"
                    value={filters.promo_type}
                    options={facets.promo_types || []}
                    onChange={(v) => setFilters({ ...filters, promo_type: v })}
                    testid="eoss-filter-promo"
                />
                <FilterSelect
                    label="State"
                    value={filters.state}
                    options={facets.states || []}
                    onChange={(v) => setFilters({ ...filters, state: v })}
                    testid="eoss-filter-state"
                />
                <FilterSelect
                    label="Distributor"
                    value={filters.distributor}
                    options={facets.distributors || []}
                    onChange={(v) => setFilters({ ...filters, distributor: v })}
                    testid="eoss-filter-dist"
                />
                <FilterSelect
                    label="Category"
                    value={filters.category}
                    options={facets.categories || []}
                    onChange={(v) => setFilters({ ...filters, category: v })}
                    testid="eoss-filter-cat"
                />
            </FilterBar>

            <ReportTable testid="eoss-table">
                <ReportTHead>
                    <SortableTH label="Date" field="invoice_date" sort={sort} setSort={setSort} />
                    <SortableTH label="Promo" field="promo_type" sort={sort} setSort={setSort} />
                    <SortableTH label="State" field="state" sort={sort} setSort={setSort} />
                    <SortableTH label="Distributor" field="distributor" sort={sort} setSort={setSort} />
                    <SortableTH label="Retailer" field="retailer" sort={sort} setSort={setSort} />
                    <SortableTH label="Invoice" field="invoice_no" sort={sort} setSort={setSort} />
                    <SortableTH label="Cat" field="category" sort={sort} setSort={setSort} />
                    <SortableTH label="Qty" field="qty" sort={sort} setSort={setSort} align="right" />
                    <SortableTH label="MRP" field="mrp_val" sort={sort} setSort={setSort} align="right" />
                    <SortableTH label="Discount" field="discount" sort={sort} setSort={setSort} align="right" />
                    <SortableTH label="Net" field="net_value" sort={sort} setSort={setSort} align="right" />
                </ReportTHead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} data-testid={`eoss-row-${r.id}`} className="border-t border-border hover:bg-muted/40">
                            <td className="mono-num px-3 py-1.5 text-muted-foreground">{r.invoice_date}</td>
                            <td className="px-3 py-1.5">
                                <span
                                    className={
                                        "rounded-sm border px-1.5 py-[1px] text-[10px] font-medium " +
                                        (PROMO_STYLES[r.promo_type] || "")
                                    }
                                >
                                    {r.promo_type}
                                </span>
                            </td>
                            <td className="px-3 py-1.5">{r.state}</td>
                            <td className="px-3 py-1.5 text-muted-foreground">{r.distributor}</td>
                            <td className="px-3 py-1.5">{r.retailer}</td>
                            <td className="mono-num px-3 py-1.5">{r.invoice_no}</td>
                            <td className="px-3 py-1.5">{r.category}</td>
                            <td className="mono-num px-3 py-1.5 text-right">{fmtNum(r.qty)}</td>
                            <td className="mono-num px-3 py-1.5 text-right">{fmtINR(r.mrp_val)}</td>
                            <td className="mono-num px-3 py-1.5 text-right text-amber-600">{fmtINR(r.discount)}</td>
                            <td className="mono-num px-3 py-1.5 text-right font-semibold">{fmtINR(r.net_value)}</td>
                        </tr>
                    ))}
                </tbody>
            </ReportTable>
        </div>
    );
}

function Stat({ label, value, tone }) {
    const c = tone === "amber" ? "text-amber-600" : tone === "red" ? "text-red-500" : "";
    return (
        <div className="rounded-sm border border-border bg-card p-3">
            <div className="mlabel">{label}</div>
            <div className={`mono-num mt-1 text-xl font-semibold ${c}`}>{value}</div>
        </div>
    );
}
